import { useEffect, useState } from "react";
import {
  CounterLpVault,
  formatCompact,
  fromMicro,
  type EnrichedMarket,
} from "@axiome/counter-lp-sdk";
import { friendlyError } from "../lib/friendlyError";

export function ProvidePanel(props: {
  vault: CounterLpVault;
  market: EnrichedMarket;
  account: string | null;
  /** When true, provide button requires successful on-chain preview */
  requirePreview?: boolean;
  onProvide: (axm: string) => Promise<void>;
}) {
  const [amount, setAmount] = useState("100");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [previewOk, setPreviewOk] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);

  const m = props.market;
  const axmNum = Number(amount) || 0;
  const projectedYearAxm = (axmNum * m.effectiveApr) / 100;
  const needPreview = props.requirePreview !== false;
  const canSubmit =
    !!props.account && !busy && axmNum > 0 && (!needPreview || previewOk);

  useEffect(() => {
    let alive = true;
    if (!amount || axmNum <= 0) {
      setPreviewToken(null);
      setPreviewOk(false);
      setPreviewReady(true);
      return;
    }
    setPreviewReady(false);
    props.vault
      .preview(m.symbol, amount)
      .then((p) => {
        if (!alive) return;
        setPreviewToken(fromMicro(p.token_needed, m.tokenDecimals));
        setPreviewOk(!!p.can_provide);
        setPreviewReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setPreviewToken(null);
        setPreviewOk(false);
        setPreviewReady(true);
      });
    return () => {
      alive = false;
    };
  }, [amount, axmNum, m.symbol, m.tokenDecimals, props.vault]);

  return (
    <div className="provide-box">
      <h2 style={{ margin: 0 }}>Внести ликвидность</h2>
      <p className="lead" style={{ marginTop: 6 }}>
        Вы вносите только AXM. Токен {m.symbol} добавляет платформа. Ожидаемая доходность:{" "}
        <span style={{ color: "var(--purple-2)" }}>{m.effectiveApr.toFixed(1)}%</span> годовых
      </p>

      <div className="field">
        <label>Сумма в AXM</label>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
          placeholder="0.0"
        />
      </div>

      <div className="asset-split">
        <div>
          <div className="lbl">Вы вносите</div>
          <div className="nums">{formatCompact(axmNum)} AXM</div>
        </div>
        <div>
          <div className="lbl">Платформа добавляет</div>
          <div className="nums">
            {previewToken != null
              ? `${formatCompact(Number(previewToken))} ${m.symbol}`
              : axmNum > 0
                ? "Считаем…"
                : `—`}
          </div>
        </div>
      </div>

      <p className="hint" style={{ marginTop: 12 }}>
        При текущей доходности ориентир за год: ~{formatCompact(projectedYearAxm)} AXM.
        При выводе позиции — AXM. Комиссии пула (AXM и {m.symbol}) делятся по долям: ваша
        часть доступна через «Получить доход».
      </p>

      {needPreview && previewReady && axmNum > 0 && !previewOk ? (
        <p className="hint" style={{ color: "var(--danger)" }}>
          Сейчас внесение временно недоступно — попробуйте позже или другую сумму
        </p>
      ) : null}

      {err ? <p className="status err">{err}</p> : null}

      <div className="cta-row">
        <button
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={async () => {
            setErr(null);
            setBusy(true);
            try {
              await props.onProvide(amount);
            } catch (e) {
              setErr(friendlyError(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Подтвердите в кошельке…" : "Внести AXM"}
        </button>
      </div>
    </div>
  );
}
